// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling — client-side call state. One reactive singleton (a call
// outlives every route, so it cannot live in a page) fed by:
//   - ActionCable events (EVENTS.1..4), re-emitted on the mitt bus by
//     helper/actionCable.js and wired in kaptionCallEvents.js
//   - REST actions (API.1..7) through an injectable `api`
//   - the media client (WEB.8) through an injectable `createClient`
// The mock layer swaps `api` and `createClient` and fires fake events on the
// same bus, so every state can be driven locally without a backend or bridge.
import { reactive, computed, readonly } from 'vue';
import {
  CALL_STATUS,
  CALL_AVAILABILITY,
  normalizeCall,
  mergeCallUpdate,
  isFinalStatus,
  callMatchesView,
  CALL_VIEWS,
  PASS_MODE,
  PASS_STATUS,
  isOpenPassStatus,
  mergePass,
} from './kaptionCallModel';
import KaptionCallClient, {
  CLIENT_STATE,
  MEDIA_FAILURE,
} from './KaptionCallClient';
import KaptionCallsAPI from 'dashboard/api/kaptionCalls';

export const ERROR_CODES = Object.freeze({
  ALREADY_ANSWERED: 'ALREADY_ANSWERED',
  ACCEPT_FAILED: 'ACCEPT_FAILED',
  MEDIA_FAILED: 'MEDIA_FAILED',
  START_FAILED: 'START_FAILED',
  // whatsapp-calling-ownership.PASS.*
  PASS_FAILED: 'PASS_FAILED',
  PASS_IN_PROGRESS: 'PASS_IN_PROGRESS',
  PASS_TARGET_UNAVAILABLE: 'PASS_TARGET_UNAVAILABLE',
  PASS_DECLINED: 'PASS_DECLINED',
  PASS_TIMEOUT: 'PASS_TIMEOUT',
  PASS_TARGET_LEFT: 'PASS_TARGET_LEFT',
  PASS_ACCEPT_FAILED: 'PASS_ACCEPT_FAILED',
  UNDO_EXPIRED: 'UNDO_EXPIRED',
  LINE_FAILED: 'LINE_FAILED',
  // whatsapp-calling-ownership.OUTBOUND.4
  ALREADY_ON_CALL: 'ALREADY_ON_CALL',
  PERMISSION_REQUEST_SENT: 'PERMISSION_REQUEST_SENT',
  PERMISSION_REQUEST_FAILED: 'PERMISSION_REQUEST_FAILED',
  AVAILABILITY_FAILED: 'AVAILABILITY_FAILED',
  // whatsapp-call-transcripts.REC.UI
  NOTICE_FAILED: 'NOTICE_FAILED',
  STOP_RECORDING_FAILED: 'STOP_RECORDING_FAILED',
});

const unwrap = response => response?.data ?? response;

const detectDevice = () =>
  typeof window !== 'undefined' && window.rumoDesktop ? 'desktop' : 'web';

export const createKaptionCallStore = (initialDeps = {}) => {
  const deps = {
    api: KaptionCallsAPI,
    createClient: options => new KaptionCallClient(options),
    getCurrentUserId: () => null,
    getMediaPreferences: async () => ({}),
    notify: () => {},
    device: detectDevice(),
    // whatsapp-calling.BRIDGE.6 — the join never waits longer on preferences.
    preferencesTimeoutMs: 1000,
    ...initialDeps,
  };

  const state = reactive({
    calls: {}, // id → normalised call
    ringingIds: [], // calls ringing on THIS device, oldest first
    activeCallId: null,
    heldCallId: null,
    pendingAcceptId: null,
    clientState: CLIENT_STATE.IDLE,
    // whatsapp-calling.WEB.9 — null | 'mic_blocked' | 'audio_failed' for the active call.
    mediaError: null,
    muted: false,
    onHold: false,
    minimised: false,
    availability: CALL_AVAILABILITY.AVAILABLE,
    agentAvailability: {}, // user_id → availability (EVENTS.4)
    list: { ids: [], view: 'all', page: 1, hasMore: false, loading: false },
    selectedCallId: null,
    micLabel: '',
    inboxCalling: {}, // inbox_id → enabled, learnt from the Calling settings
    // whatsapp-calling-ownership.RING.5 — calls ringing elsewhere while I am
    // busy: they only feed "N waiting", never the toast.
    waitingIds: [],
    // whatsapp-calling-ownership.PASS.* — pass id → normalised pass.
    passes: {},
    incomingPassIds: [], // passes ringing for me (target)
    outgoingPassId: null, // the pass I started on my active call
    passedNotice: null, // {passId, callId, toUser, undoUntil, onItSeconds}
    unmutePassId: null, // pass_now I accepted: report the first unmute
    // whatsapp-calling-ownership.LINE.3 — inbox_id → line.
    lines: {},
  });

  // Timer that stops the passer's silent media when the undo window closes.
  let passedTimer = null;

  // Media clients are not reactive (RTCPeerConnection inside).
  const clients = new Map();
  // whatsapp-calling.WEB.9 — the join payload per call, so Retry can re-join
  // with the same participant token.
  const joinPayloads = new Map();

  const configure = (overrides = {}) => Object.assign(deps, overrides);
  const myId = () => deps.getCurrentUserId();

  const upsert = raw => {
    const call = normalizeCall(raw);
    if (!call?.id) return null;
    const known = state.calls[call.id];
    if (!known) {
      state.calls[call.id] = call;
      return state.calls[call.id];
    }
    // A replayed or out-of-order payload never moves a call backwards.
    const merged = { ...known };
    Object.entries(call).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (key === 'status') return;
      merged[key] = value;
    });
    state.calls[call.id] = mergeCallUpdate(merged, { status: call.status });
    return state.calls[call.id];
  };

  const stopRinging = id => {
    state.ringingIds = state.ringingIds.filter(rid => rid !== id);
  };

  const resetMediaFlags = () => {
    state.muted = false;
    state.onHold = false;
    state.clientState = CLIENT_STATE.IDLE;
    state.mediaError = null;
  };

  const teardown = async id => {
    const client = clients.get(id);
    clients.delete(id);
    joinPayloads.delete(id);
    if (state.passedNotice?.callId === id) {
      clearTimeout(passedTimer);
      state.passedNotice = null;
    }
    const outgoing = state.passes[state.outgoingPassId];
    if (outgoing?.callId === id) state.outgoingPassId = null;
    if (state.passes[state.unmutePassId]?.callId === id)
      state.unmutePassId = null;
    state.waitingIds = state.waitingIds.filter(wid => wid !== id);
    if (client) await client.stop();
    if (state.heldCallId === id) state.heldCallId = null;
    if (state.activeCallId !== id) return;
    state.activeCallId = null;
    resetMediaFlags();
    // Resume the call that was put on hold for "Hold + answer" (WEB.1).
    if (state.heldCallId) {
      const heldId = state.heldCallId;
      state.heldCallId = null;
      state.activeCallId = heldId;
      const held = clients.get(heldId);
      held?.setHold(false);
      state.clientState = held?.state || CLIENT_STATE.CONNECTED;
    }
  };

  // ---- PASS (whatsapp-calling-ownership.PASS.*) ---------------------------

  const removeIncomingPass = passId => {
    state.incomingPassIds = state.incomingPassIds.filter(pid => pid !== passId);
  };

  const dropPassesOf = callId => {
    state.incomingPassIds = state.incomingPassIds.filter(
      pid => state.passes[pid]?.callId !== callId
    );
  };

  // Stores a pass payload and reacts to the move it describes, from MY side.
  const applyPass = (raw, callId) => {
    const known = state.passes[raw?.id];
    const pass = mergePass(known, raw, callId);
    if (!pass) return null;
    state.passes[pass.id] = pass;
    const me = myId();
    const ended = !isOpenPassStatus(pass.status);
    if (ended) removeIncomingPass(pass.id);
    // Passer: the pass in progress is over (completion is handled by onPassed).
    if (ended && state.outgoingPassId === pass.id) state.outgoingPassId = null;
    // Target: a pass I accepted was cancelled during the consult, the passer
    // pulled it back with Undo, or failed — the customer is not mine.
    const imTarget = me && pass.toUser?.id === me;
    const dropsTarget = [
      PASS_STATUS.FAILED,
      PASS_STATUS.CANCELLED,
      PASS_STATUS.UNDONE,
    ].includes(pass.status);
    if (imTarget && dropsTarget && state.activeCallId === pass.callId) {
      if (state.unmutePassId === pass.id) state.unmutePassId = null;
      teardown(pass.callId);
    }
    return pass;
  };

  // ---- EVENTS -------------------------------------------------------------

  // whatsapp-calling.EVENTS.1
  const onRinging = payload => {
    const call = upsert(payload);
    if (!call || isFinalStatus(call.status)) return;
    if (call.status !== CALL_STATUS.RINGING) return;
    if (state.activeCallId === call.id) return;
    // whatsapp-calling-ownership.RING.5 — busy and skipped: display only.
    if (payload?.ring?.waiting_only) {
      if (!state.waitingIds.includes(call.id)) state.waitingIds.push(call.id);
      return;
    }
    if (state.availability === CALL_AVAILABILITY.FORWARD_TO_PHONE) return; // ROUTING.5
    if (!state.ringingIds.includes(call.id)) state.ringingIds.push(call.id);
  };

  // whatsapp-calling.EVENTS.2 / ROUTING.2 — every device except the one that
  // answered stops ringing.
  const onAnswered = payload => {
    const { id } = payload || {};
    if (!id) return;
    const known = state.calls[id];
    if (known) {
      state.calls[id] = mergeCallUpdate(known, {
        status: CALL_STATUS.ANSWERED,
        answered_by: payload.answered_by,
      });
    } else {
      upsert({ ...payload, status: CALL_STATUS.ANSWERED });
    }
    const answeredHere =
      state.pendingAcceptId === id || state.activeCallId === id;
    if (!answeredHere) stopRinging(id);
  };

  // whatsapp-calling.EVENTS.3
  const onUpdated = payload => {
    const { id } = payload || {};
    if (!id) return;
    const known = state.calls[id] || upsert({ id, status: payload.status });
    state.calls[id] = mergeCallUpdate(known, payload);
    const { status } = state.calls[id];
    if (status !== CALL_STATUS.RINGING) {
      stopRinging(id);
      state.waitingIds = state.waitingIds.filter(wid => wid !== id);
    }
    // ROUTING.4 / ROUTING.7 — I declined, or sent it to my phone, from
    // another tab or the desktop app: stop ringing here too.
    // whatsapp-calling-ownership.RING.1 — the ring moved to the next tier.
    const me = myId();
    if (
      me &&
      (payload.declined_by_user_id === me ||
        payload.forwarded_to_phone_user_id === me ||
        (payload.stopped_ringing_user_ids || []).includes(me))
    )
      stopRinging(id);
    if (payload.pass) applyPass(payload.pass, id);
    if (isFinalStatus(status)) {
      dropPassesOf(id);
      teardown(id);
    }
  };

  const onEnded = payload => onUpdated(payload);

  // whatsapp-call-transcripts.EVENTS.2 — {call_id, transcript_status}: only
  // calls this tab knows move (the transcript store refetches open ones).
  const onTranscriptUpdated = payload => {
    const id = payload?.call_id ?? payload?.id;
    if (!id || !state.calls[id]) return;
    state.calls[id] = mergeCallUpdate(state.calls[id], {
      transcript_status: payload.transcript_status,
      recording_status: payload.recording_status,
    });
  };

  // whatsapp-call-transcripts.REC.UI — notice mode 'button', and "Stop
  // recording" (CONSENT.4). Both answer with the call JSON.
  const recordingAction = async (id, request, failCode) => {
    if (!id) return null;
    try {
      const data = unwrap(await request());
      return data?.id ? upsert(data) : state.calls[id] || null;
    } catch (e) {
      deps.notify(failCode);
      return null;
    }
  };
  const playRecordingNotice = (id = state.activeCallId) =>
    recordingAction(
      id,
      () => deps.api.playRecordingNotice(id),
      ERROR_CODES.NOTICE_FAILED
    );
  const stopRecording = (id = state.activeCallId) =>
    recordingAction(
      id,
      () => deps.api.setRecording(id, { on: false }),
      ERROR_CODES.STOP_RECORDING_FAILED
    );

  // PASS.3 / UI.4 — a teammate is passing me a call.
  const onPassRinging = payload => {
    if (!payload?.pass) return;
    const { pass: rawPass, ...rawCall } = payload;
    const call = upsert(rawCall);
    if (!call) return;
    const pass = applyPass(rawPass, call.id);
    if (!pass || pass.status !== PASS_STATUS.RINGING) return;
    if (state.activeCallId === call.id) return;
    if (!state.incomingPassIds.includes(pass.id))
      state.incomingPassIds.push(pass.id);
  };

  const FAIL_CODES = {
    declined: ERROR_CODES.PASS_DECLINED,
    timeout: ERROR_CODES.PASS_TIMEOUT,
    target_left: ERROR_CODES.PASS_TARGET_LEFT,
    target_busy: ERROR_CODES.PASS_TARGET_UNAVAILABLE,
  };

  // PASS.3 — the target declined or did not answer: I keep the customer.
  const onPassFailed = payload => {
    if (!payload?.pass) return;
    const pass = applyPass(payload.pass, payload.id);
    if (!pass) return;
    if (pass.reason === 'cancelled') return;
    deps.notify(FAIL_CODES[pass.reason] || ERROR_CODES.PASS_FAILED, {
      name: pass.toUser?.name || '',
    });
  };

  const stopPassedMedia = async callId => {
    clearTimeout(passedTimer);
    passedTimer = null;
    state.passedNotice = null;
    await teardown(callId);
  };

  // PASS.4 / PASS.6 / PASS.7 — the call now belongs to the target.
  const onPassed = payload => {
    const { id } = payload || {};
    if (!id || !payload.pass) return;
    const known = state.calls[id] || upsert({ id, status: 'answered' });
    state.calls[id] = mergeCallUpdate(known, {
      answered_by: payload.answered_by,
    });
    const pass = applyPass(payload.pass, id);
    if (!pass) return;
    removeIncomingPass(pass.id);
    const me = myId();
    if (state.outgoingPassId === pass.id) state.outgoingPassId = null;
    if (!me || pass.fromUser?.id !== me || state.activeCallId !== id) return;
    // I passed it. The bridge already moved the customer; keep my session
    // open and silent while Undo is possible, then leave.
    const undoMs = pass.undoUntil ? Date.parse(pass.undoUntil) - Date.now() : 0;
    const answeredAt = state.calls[id]?.answeredAt;
    state.passedNotice = {
      passId: pass.id,
      callId: id,
      toUser: pass.toUser,
      undoUntil: pass.undoUntil,
      canUndo: pass.mode === PASS_MODE.PASS_NOW && undoMs > 0,
      onItSeconds: answeredAt
        ? Math.max(0, Math.round((Date.now() - Date.parse(answeredAt)) / 1000))
        : 0,
    };
    state.muted = true;
    clients.get(id)?.setMuted(true);
    clearTimeout(passedTimer);
    passedTimer = setTimeout(
      () => stopPassedMedia(id),
      Math.max(undoMs, state.passedNotice.canUndo ? 0 : 3000)
    );
  };

  // whatsapp-calling-ownership.LINE.3
  const lineFromPayload = (raw, inboxName) => ({
    inboxId: raw.inbox_id,
    inboxName: raw.inbox_name || inboxName || '',
    designatedUserId: raw.designated_user_id ?? null,
    holderUserId:
      raw.holder_user_id ??
      raw.line_holder_user_id ??
      raw.designated_user_id ??
      null,
    lineHolderUserId: raw.line_holder_user_id ?? null,
    lineHolderUntil: raw.line_holder_until ?? null,
    lineHolder: raw.line_holder || null,
    designatedUser: raw.designated_user || null,
    backupUserIds: raw.backup_user_ids || [],
  });

  const storeLine = (raw, inboxId, inboxName) => {
    const id = raw?.inbox_id ?? inboxId;
    if (!id) return;
    const known = state.lines[id];
    state.lines[id] = lineFromPayload(
      { ...raw, inbox_id: id },
      inboxName || known?.inboxName
    );
  };

  const onLineChanged = payload => {
    if (!payload?.inbox_id) return;
    // Flat inbox_id/designated_user_id/line_holder_user_id/line_holder_until
    // ride next to `line`; either is enough.
    const flat = {
      designated_user_id: payload.designated_user_id,
      line_holder_user_id: payload.line_holder_user_id,
      line_holder_until: payload.line_holder_until,
    };
    Object.keys(flat).forEach(
      key => flat[key] === undefined && delete flat[key]
    );
    storeLine(
      { ...flat, ...(payload.line || {}) },
      payload.inbox_id,
      payload.inbox_name
    );
  };

  // whatsapp-calling.EVENTS.4
  const onAvailabilityChanged = ({ user_id: userId, availability } = {}) => {
    if (!userId || !availability) return;
    state.agentAvailability[userId] = availability;
    if (userId === myId()) {
      state.availability = availability;
      if (availability === CALL_AVAILABILITY.FORWARD_TO_PHONE)
        state.ringingIds = [];
    }
  };

  // ---- MEDIA --------------------------------------------------------------

  // BRIDGE.6 — device preferences are a nicety; never hold the join for them.
  const mediaPreferences = () =>
    Promise.race([
      Promise.resolve()
        .then(() => deps.getMediaPreferences())
        .catch(() => ({})),
      new Promise(resolve => {
        setTimeout(() => resolve({}), deps.preferencesTimeoutMs);
      }),
    ]);

  // whatsapp-calling.WEB.9 / API.12 — never silent: show why and tell Rails,
  // which starts the rejoin grace (PASS.8) instead of a dead line.
  const reportMediaFailure = (callId, reason) => {
    if (state.activeCallId === callId) state.mediaError = reason;
    deps.notify(ERROR_CODES.MEDIA_FAILED);
    Promise.resolve()
      .then(() =>
        deps.api.mediaFailed?.(callId, { device: deps.device, reason })
      )
      .catch(() => {});
  };

  const joinBridge = async (callId, payload) => {
    joinPayloads.set(callId, payload);
    if (state.activeCallId === callId) state.mediaError = null;
    const prefs = (await mediaPreferences()) || {};
    let joining = true;
    const client = deps.createClient({
      bridgeUrl: payload.bridge_url,
      participantToken: payload.participant_token,
      iceServers: payload.ice_servers,
      micDeviceId: prefs.micDeviceId || null,
      speakerDeviceId: prefs.speakerDeviceId || null,
      noiseSuppression: prefs.noiseSuppression || 'standard',
      noiseSuppressorProvider: prefs.noiseSuppressorProvider || null,
      onStateChange: clientState => {
        if (clients.get(callId) !== client) return;
        if (state.activeCallId === callId) state.clientState = clientState;
        // A drop after the join (the join's own failure is reported below).
        if (clientState === CLIENT_STATE.FAILED && !joining) {
          if (state.activeCallId === callId)
            state.mediaError = MEDIA_FAILURE.AUDIO_FAILED;
          deps.notify(ERROR_CODES.MEDIA_FAILED);
        }
      },
    });
    clients.set(callId, client);
    try {
      await client.start();
      state.micLabel = client.mic?.track?.label || '';
      if (state.activeCallId === callId) state.mediaError = null;
    } catch (error) {
      // The call stays answered in Rails; the agent can Retry or End.
      if (
        clients.get(callId) === client &&
        client.state !== CLIENT_STATE.CLOSED
      )
        reportMediaFailure(
          callId,
          error?.reason === MEDIA_FAILURE.MIC_BLOCKED
            ? MEDIA_FAILURE.MIC_BLOCKED
            : MEDIA_FAILURE.AUDIO_FAILED
        );
    }
    joining = false;
    if (state.activeCallId === callId) state.clientState = client.state;
  };

  // whatsapp-calling.WEB.9 — Retry: a fresh client with the same token.
  const retryMedia = async () => {
    const callId = state.activeCallId;
    const payload = callId ? joinPayloads.get(callId) : null;
    if (!payload) return false;
    const previous = clients.get(callId);
    clients.delete(callId);
    await previous?.stop();
    await joinBridge(callId, payload);
    return !state.mediaError;
  };

  const holdActiveForSecondCall = () => {
    if (!state.activeCallId) return;
    const current = clients.get(state.activeCallId);
    current?.setHold(true);
    state.heldCallId = state.activeCallId;
    state.activeCallId = null;
  };

  // ---- ACTIONS ------------------------------------------------------------

  // whatsapp-calling.API.2 / WEB.1 — Answer, or "Hold + answer" when a call
  // is already active.
  const accept = async id => {
    if (!id || state.pendingAcceptId) return false;
    state.pendingAcceptId = id;
    try {
      const payload = unwrap(
        await deps.api.accept(id, { device: deps.device })
      );
      stopRinging(id);
      holdActiveForSecondCall();
      state.activeCallId = id;
      state.muted = false;
      state.onHold = false;
      state.minimised = false;
      const known = state.calls[id];
      if (known) {
        state.calls[id] = mergeCallUpdate(known, {
          status: CALL_STATUS.ANSWERED,
          answered_at: known.answeredAt || new Date().toISOString(),
          answered_by: payload?.answered_by || { id: myId(), name: '' },
        });
      }
      await joinBridge(id, payload || {});
      return true;
    } catch (error) {
      stopRinging(id);
      deps.notify(
        error?.response?.status === 409
          ? ERROR_CODES.ALREADY_ANSWERED
          : ERROR_CODES.ACCEPT_FAILED
      );
      return false;
    } finally {
      state.pendingAcceptId = null;
    }
  };

  // whatsapp-calling.API.3 / ROUTING.4 — decline for myself only.
  const decline = async (id, { forwardToPhone = false } = {}) => {
    stopRinging(id);
    try {
      await deps.api.decline(id, { device: deps.device, forwardToPhone });
    } catch (e) {
      // The ring budget (ROUTING.3) closes it server-side anyway.
    }
  };

  // Local silence only (desktop forwarding rule, DESKTOP.5).
  const silence = id => stopRinging(id);

  // whatsapp-calling.API.3
  const end = async (id = state.activeCallId) => {
    if (!id) return;
    const known = state.calls[id];
    try {
      await deps.api.end(id);
    } catch (e) {
      // keep tearing down locally
    }
    if (known && !isFinalStatus(known.status)) {
      state.calls[id] = mergeCallUpdate(known, {
        status:
          known.status === CALL_STATUS.ANSWERED
            ? CALL_STATUS.COMPLETED
            : CALL_STATUS.MISSED,
        ended_at: new Date().toISOString(),
      });
    }
    await teardown(id);
  };

  // whatsapp-calling-ownership.PASS.6 — after a pass_now the target starts
  // muted; the first unmute closes the passer's Undo window.
  const reportUnmute = () => {
    const passId = state.unmutePassId;
    if (state.muted || !passId) return;
    state.unmutePassId = null;
    const pass = state.passes[passId];
    deps.api.passUnmuted?.(pass?.callId, passId)?.catch?.(() => {});
  };

  const toggleMute = () => {
    state.muted = !state.muted;
    clients.get(state.activeCallId)?.setMuted(state.muted);
    reportUnmute();
  };

  const setMuted = muted => {
    state.muted = !!muted;
    clients.get(state.activeCallId)?.setMuted(state.muted);
    reportUnmute();
  };

  const toggleHold = () => {
    state.onHold = !state.onHold;
    clients.get(state.activeCallId)?.setHold(state.onHold);
  };

  const setMinimised = value => {
    state.minimised = !!value;
  };

  const errorCode = error => error?.response?.data?.error;

  // whatsapp-calling-ownership.PASS.1 — teammates for the picker.
  const loadPassTargets = async (callId = state.activeCallId) => {
    if (!callId) return [];
    try {
      const data = unwrap(await deps.api.passTargets(callId));
      return data?.payload || [];
    } catch (e) {
      return [];
    }
  };

  const START_PASS_ERRORS = {
    pass_in_progress: ERROR_CODES.PASS_IN_PROGRESS,
    target_unavailable: ERROR_CODES.PASS_TARGET_UNAVAILABLE,
  };

  // whatsapp-calling-ownership.PASS.2
  const startPass = async ({
    toUserId,
    mode = PASS_MODE.PASS_NOW,
    note = '',
    assignConversation = true,
  } = {}) => {
    const callId = state.activeCallId;
    if (!callId || !toUserId) return false;
    try {
      const data = unwrap(
        await deps.api.pass(callId, {
          to_user_id: toUserId,
          mode,
          note,
          assign_conversation: assignConversation,
        })
      );
      const pass = applyPass(
        data?.pass || {
          id: `local-${Date.now()}`,
          status: PASS_STATUS.RINGING,
          mode,
          to_user: { id: toUserId },
        },
        callId
      );
      state.outgoingPassId = pass?.id ?? null;
      return true;
    } catch (error) {
      deps.notify(
        START_PASS_ERRORS[errorCode(error)] || ERROR_CODES.PASS_FAILED
      );
      return false;
    }
  };

  const passAction = async (method, passId) => {
    const pass = state.passes[passId];
    if (!pass) return null;
    try {
      const data = unwrap(await deps.api[method](pass.callId, passId));
      // Contract: complete/cancel/undo/decline/unmuted answer with the CALL
      // JSON whose `pass` is the full pass; {pass, call} is still accepted.
      const callJson = data?.call || (data?.id && data?.pass ? data : null);
      if (callJson) upsert(callJson);
      if (data?.pass) applyPass(data.pass, pass.callId);
      return data || {};
    } catch (error) {
      if (errorCode(error) === 'undo_expired')
        deps.notify(ERROR_CODES.UNDO_EXPIRED);
      else deps.notify(ERROR_CODES.PASS_FAILED);
      return null;
    }
  };

  // PASS.5 — talk_first: "Pass to <name>" / "Back to <customer>".
  const completePass = () => passAction('completePass', state.outgoingPassId);

  // whatsapp-calling-ai.UI.3 / AI_PASS.4 — the agent being briefed by the AI
  // takes the call ("Take the call"): completes the pass the AI started.
  const takeCall = async pass => {
    if (!pass?.id || !pass.callId) return null;
    try {
      const data = unwrap(await deps.api.completePass(pass.callId, pass.id));
      const callJson = data?.call || (data?.id && data?.pass ? data : null);
      if (callJson) upsert(callJson);
      if (data?.pass) applyPass(data.pass, pass.callId);
      return data || {};
    } catch (error) {
      deps.notify(ERROR_CODES.PASS_FAILED);
      return null;
    }
  };

  const cancelPass = async () => {
    const passId = state.outgoingPassId;
    const result = await passAction('cancelPass', passId);
    if (result && state.outgoingPassId === passId) state.outgoingPassId = null;
    return result;
  };

  // PASS.6 — Undo within the window: the customer comes back to me.
  const undoPass = async () => {
    const notice = state.passedNotice;
    if (!notice) return false;
    const result = await passAction('undoPass', notice.passId);
    if (!result) return false;
    clearTimeout(passedTimer);
    passedTimer = null;
    state.passedNotice = null;
    const known = state.calls[notice.callId];
    if (known)
      state.calls[notice.callId] = mergeCallUpdate(known, {
        answered_by: { id: myId(), name: known.answeredBy?.name || '' },
      });
    state.muted = false;
    clients.get(notice.callId)?.setMuted(false);
    return true;
  };

  // PASS.3 / PASS.4 / PASS.5 — the target answers a pass.
  const acceptPass = async passId => {
    const pass = state.passes[passId];
    if (!pass || state.pendingAcceptId) return false;
    state.pendingAcceptId = pass.callId;
    try {
      const payload = unwrap(
        await deps.api.acceptPass(pass.callId, passId, { device: deps.device })
      );
      removeIncomingPass(passId);
      if (payload?.pass) applyPass(payload.pass, pass.callId);
      if (payload?.call) upsert(payload.call);
      holdActiveForSecondCall();
      state.activeCallId = pass.callId;
      state.onHold = false;
      state.minimised = false;
      // PASS.6 — pass_now: start muted until I speak (closes Undo).
      const passNow = pass.mode === PASS_MODE.PASS_NOW;
      state.muted = passNow;
      state.unmutePassId = passNow ? passId : null;
      await joinBridge(pass.callId, payload || {});
      if (passNow) clients.get(pass.callId)?.setMuted(true);
      return true;
    } catch (error) {
      removeIncomingPass(passId);
      deps.notify(ERROR_CODES.PASS_ACCEPT_FAILED);
      return false;
    } finally {
      state.pendingAcceptId = null;
    }
  };

  // PASS.3 — decline (or "Leave" during the consult: the call goes back).
  const declinePass = async passId => {
    const pass = state.passes[passId];
    removeIncomingPass(passId);
    if (!pass) return;
    try {
      await deps.api.declinePass(pass.callId, passId);
    } catch (e) {
      // the 30 s pass timeout settles it server-side
    }
    if (state.activeCallId === pass.callId && pass.toUser?.id === myId())
      await teardown(pass.callId);
  };

  // ---- LINE (whatsapp-calling-ownership.LINE.3) ---------------------------

  const loadLines = async () => {
    try {
      const data = unwrap(await deps.api.getCallLines());
      (data?.payload || []).forEach(line => storeLine(line));
    } catch (e) {
      // no lines: the status menu shows nothing
    }
  };

  const lineResult = async (inboxId, request) => {
    try {
      const data = unwrap(await request());
      if (data?.line) storeLine(data.line, inboxId);
      return true;
    } catch (e) {
      deps.notify(ERROR_CODES.LINE_FAILED);
      return false;
    }
  };

  // until: 'until_taken_back' | 'end_of_day' | unix seconds.
  const passLine = (inboxId, { userId, until }) =>
    lineResult(inboxId, () => deps.api.passLine(inboxId, { userId, until }));

  const takeLineBack = inboxId =>
    lineResult(inboxId, () => deps.api.takeLineBack(inboxId));

  // whatsapp-calling.API.4 / WEB.5. Returns 'started' | 'permission_required'
  // | 'failed'.
  // whatsapp-calling-ownership.OUTBOUND.* — a 403 not_assignee resolves to
  // {status: 'not_assignee', assignee}.
  const startOutbound = async (conversationId, contact = {}) => {
    try {
      const payload = unwrap(
        await deps.api.startOutbound(conversationId, { device: deps.device })
      );
      if (payload?.status === 'permission_required')
        return 'permission_required';
      const callId = payload?.call?.id ?? payload?.call_id ?? payload?.id;
      upsert({
        ...(payload?.call || {}),
        id: callId,
        conversation_id: conversationId,
        direction: 'outbound',
        status: payload?.call?.status || CALL_STATUS.RINGING,
        contact_name: payload?.call?.contact_name || contact.name,
        contact_avatar_url:
          payload?.call?.contact_avatar_url || contact.thumbnail,
        inbox_name: payload?.call?.inbox_name || contact.inboxName,
        started_at: new Date().toISOString(),
      });
      holdActiveForSecondCall();
      state.activeCallId = callId;
      state.minimised = false;
      await joinBridge(callId, payload);
      return 'started';
    } catch (error) {
      const code = errorCode(error);
      if (code === 'not_assignee')
        return {
          status: 'not_assignee',
          assignee: error.response.data.assignee || null,
        };
      deps.notify(
        code === 'already_on_call' || code === 'agent_call_in_progress'
          ? ERROR_CODES.ALREADY_ON_CALL
          : ERROR_CODES.START_FAILED
      );
      return 'failed';
    }
  };

  // whatsapp-calling-ownership.OUTBOUND.5 — the server's decision for me:
  // {can_call, reason, assigns, assignee}, or null when it cannot be read.
  const loadOutboundEligibility = async conversationId => {
    try {
      return unwrap(await deps.api.outboundEligibility(conversationId)) || null;
    } catch (e) {
      return null;
    }
  };

  // whatsapp-calling.API.5
  const requestPermission = async conversationId => {
    try {
      await deps.api.requestPermission(conversationId);
      deps.notify(ERROR_CODES.PERMISSION_REQUEST_SENT);
      return true;
    } catch (e) {
      deps.notify(ERROR_CODES.PERMISSION_REQUEST_FAILED);
      return false;
    }
  };

  // whatsapp-calling.API.6 / ROUTING.5
  const setAvailability = async availability => {
    const previous = state.availability;
    state.availability = availability;
    if (availability === CALL_AVAILABILITY.FORWARD_TO_PHONE)
      state.ringingIds = [];
    try {
      await deps.api.setAvailability(availability);
      const me = myId();
      if (me) state.agentAvailability[me] = availability;
      return true;
    } catch (e) {
      state.availability = previous;
      deps.notify(ERROR_CODES.AVAILABILITY_FAILED);
      return false;
    }
  };

  const hydrateAvailability = availability => {
    if (availability) state.availability = availability;
  };

  // whatsapp-calling.API.6 — GET /kaption/call_availability.
  const loadAvailability = async () => {
    try {
      const data = unwrap(await deps.api.getAvailability());
      hydrateAvailability(data?.availability);
    } catch (e) {
      // keep the current value; PATCH still works
    }
  };

  // whatsapp-calling.API.1 — Calls workspace list.
  const loadCalls = async ({ view = 'all', page = 1 } = {}) => {
    state.list.loading = true;
    state.list.view = view;
    try {
      const filter = CALL_VIEWS[view] || {};
      const data = unwrap(await deps.api.list({ ...filter, page }));
      const rows = data?.payload || data?.data || data?.calls || data || [];
      const ids = (Array.isArray(rows) ? rows : [])
        .map(row => upsert(row)?.id)
        .filter(Boolean);
      state.list.ids = page > 1 ? [...state.list.ids, ...ids] : ids;
      state.list.page = page;
      // API.1 — meta {page, per_page, total_count}.
      const meta = data?.meta || {};
      state.list.hasMore = !!(
        meta.next_page ||
        meta.has_more ||
        (meta.total_count && meta.per_page
          ? page * meta.per_page < meta.total_count
          : false)
      );
    } finally {
      state.list.loading = false;
    }
  };

  const loadCall = async id => {
    const data = unwrap(await deps.api.show(id));
    return upsert(data?.payload || data);
  };

  // WEB.5 — whether an inbox can place calls. The inbox payload's
  // `kaption_calling_enabled` wins; this map covers inboxes whose settings
  // were just loaded or saved (and the mock layer).
  const setInboxCalling = (inboxId, enabled) => {
    if (inboxId) state.inboxCalling[inboxId] = !!enabled;
  };

  const isInboxCallingEnabled = inbox => {
    if (!inbox?.id) return false;
    if (typeof inbox.kaption_calling_enabled === 'boolean')
      return inbox.kaption_calling_enabled || !!state.inboxCalling[inbox.id];
    return !!state.inboxCalling[inbox.id];
  };

  const selectCall = id => {
    state.selectedCallId = id;
  };

  // ---- GETTERS ------------------------------------------------------------

  const activeCall = computed(() =>
    state.activeCallId ? state.calls[state.activeCallId] : null
  );
  const heldCall = computed(() =>
    state.heldCallId ? state.calls[state.heldCallId] : null
  );
  const ringingCalls = computed(() =>
    state.ringingIds.map(id => state.calls[id]).filter(Boolean)
  );
  const allCalls = computed(() => Object.values(state.calls));
  const listedCalls = computed(() => {
    // Live events add calls the list has not fetched yet; keep the view's
    // filter and newest first.
    const listed = new Set(state.list.ids);
    allCalls.value.forEach(call => {
      if (callMatchesView(call, state.list.view)) listed.add(call.id);
    });
    return [...listed]
      .map(id => state.calls[id])
      .filter(call => call && callMatchesView(call, state.list.view))
      .sort((a, b) =>
        String(b.ringStartedAt || b.startedAt || '').localeCompare(
          String(a.ringStartedAt || a.startedAt || '')
        )
      );
  });
  const counts = computed(() => ({
    ringing: allCalls.value.filter(c => c.status === CALL_STATUS.RINGING)
      .length,
    onCall: allCalls.value.filter(c => c.status === CALL_STATUS.ANSWERED)
      .length,
  }));

  // whatsapp-calling-ownership.RING.5 — "N waiting" in the dock.
  const waitingCount = computed(
    () =>
      state.ringingIds.length +
      state.waitingIds.filter(id => !state.ringingIds.includes(id)).length
  );

  const passWithCall = pass =>
    pass ? { ...pass, call: state.calls[pass.callId] || null } : null;

  // UI.4 — passes ringing for me, oldest first.
  const incomingPasses = computed(() =>
    state.incomingPassIds
      .map(id => passWithCall(state.passes[id]))
      .filter(pass => pass?.call)
  );
  // UI.3 — the pass in progress on my call (ringing or consulting).
  const outgoingPass = computed(() =>
    state.outgoingPassId ? state.passes[state.outgoingPassId] || null : null
  );
  const passedNotice = computed(() => state.passedNotice);

  // UI.2 — lines I hold now / lines I designate that someone else holds.
  const allLines = computed(() => Object.values(state.lines));
  const heldLines = computed(() =>
    allLines.value.filter(line => myId() && line.holderUserId === myId())
  );
  const passedLines = computed(() =>
    allLines.value.filter(
      line =>
        myId() &&
        line.designatedUserId === myId() &&
        line.holderUserId &&
        line.holderUserId !== myId()
    )
  );

  const findCall = id => (id ? state.calls[id] || null : null);

  const reset = async () => {
    await Promise.all([...clients.values()].map(c => c.stop()));
    clients.clear();
    joinPayloads.clear();
    Object.assign(state, {
      calls: {},
      ringingIds: [],
      activeCallId: null,
      heldCallId: null,
      pendingAcceptId: null,
      clientState: CLIENT_STATE.IDLE,
      mediaError: null,
      muted: false,
      onHold: false,
      minimised: false,
      availability: CALL_AVAILABILITY.AVAILABLE,
      agentAvailability: {},
      list: { ids: [], view: 'all', page: 1, hasMore: false, loading: false },
      selectedCallId: null,
      micLabel: '',
      inboxCalling: {},
      waitingIds: [],
      passes: {},
      incomingPassIds: [],
      outgoingPassId: null,
      passedNotice: null,
      unmutePassId: null,
      lines: {},
    });
    clearTimeout(passedTimer);
    passedTimer = null;
  };

  return {
    state: readonly(state),
    activeCall,
    heldCall,
    ringingCalls,
    listedCalls,
    counts,
    waitingCount,
    incomingPasses,
    outgoingPass,
    passedNotice,
    heldLines,
    passedLines,
    configure,
    api: () => deps.api,
    getClient: id => clients.get(id),
    findCall,
    // events
    onRinging,
    onAnswered,
    onUpdated,
    onEnded,
    onTranscriptUpdated,
    onAvailabilityChanged,
    onPassRinging,
    onPassFailed,
    onPassed,
    onLineChanged,
    // actions
    accept,
    decline,
    silence,
    end,
    toggleMute,
    setMuted,
    toggleHold,
    setMinimised,
    loadPassTargets,
    startPass,
    completePass,
    takeCall,
    cancelPass,
    undoPass,
    acceptPass,
    declinePass,
    loadLines,
    passLine,
    takeLineBack,
    startOutbound,
    retryMedia,
    playRecordingNotice,
    stopRecording,
    loadOutboundEligibility,
    requestPermission,
    setAvailability,
    hydrateAvailability,
    loadAvailability,
    loadCalls,
    loadCall,
    selectCall,
    setInboxCalling,
    isInboxCallingEnabled,
    upsert,
    reset,
  };
};

let singleton = null;

export const useKaptionCallStore = () => {
  if (!singleton) singleton = createKaptionCallStore();
  return singleton;
};
